import { forwardRef, type ButtonHTMLAttributes } from 'react';

/**
 * PillButton — small, tonal outline (or filled) action button.
 *
 * This is the design-system home for the "small colored-outline button"
 * pattern that pages used to hand-roll as in-file `smallButton(color)` /
 * `pillButton(color, opts)` style helpers (see Track E). The look is preserved
 * exactly: a 1px solid border in the tone color, transparent (or tone-filled)
 * background, no hover transition.
 *
 * Use `<Button>` for the primary/secondary page actions; use `<PillButton>` for
 * the compact tonal actions inside tables, rows, toolbars, and job lists.
 */

export type PillTone = 'accent' | 'success' | 'danger' | 'warning' | 'neutral' | 'muted';
export type PillSize = 'xs' | 'sm';

export interface PillButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Semantic color of the border / text (and fill when `filled`). */
  tone?: PillTone;
  /** `xs` (3×8, 11px) matches the old `smallButton`; `sm` (4×10, 12px) the old `pillButton`. */
  size?: PillSize;
  /** Solid tone background with light text instead of an outline. */
  filled?: boolean;
}

const TONE_VAR: Record<PillTone, string> = {
  accent: 'var(--accent)',
  success: 'var(--green)',
  danger: 'var(--red)',
  warning: 'var(--yellow)',
  neutral: 'var(--text-secondary)',
  muted: 'var(--text-muted)',
};

const SIZE: Record<PillSize, React.CSSProperties> = {
  xs: { padding: '3px 8px', fontSize: 11 },
  sm: { padding: '4px 10px', fontSize: 12 },
};

export const PillButton = forwardRef<HTMLButtonElement, PillButtonProps>(function PillButton(
  { tone = 'neutral', size = 'xs', filled = false, disabled, style, children, ...rest },
  ref,
) {
  const color = TONE_VAR[tone];
  return (
    <button
      ref={ref}
      disabled={disabled}
      style={{
        ...SIZE[size],
        borderRadius: 4,
        border: `1px solid ${color}`,
        background: filled ? color : 'transparent',
        color: filled ? 'var(--bg-primary, white)' : color,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        fontFamily: 'inherit',
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
});
